// Veiligheid-module - gedeelde DTO's (API -> UI). Zod-request-schemas leven per
// route in apps/api; de RCA-structuur staat hier (vast schema, gedeeld door API + UI).

import { z } from 'zod'

export type VeiligheidsProcedureType =
  | 'LMRA'
  | 'TRA'
  | 'LOTOTO'
  | 'CONTROLELIJST_GEREEDSCHAPSKOFFER'
  | 'CONTROLELIJST_GEREEDSCHAPSWAGEN'
  | 'INSPECTIE_VOERTUIG'
  | 'INSPECTIE_PBM'
  | 'RCA'
  // batch-68 gespreksvoorbereiding (FO-346 §3.6) — toolboxbijeenkomst met deelnemerslijst.
  | 'TOOLBOX'
export type VeiligheidsItemType =
  | 'JA_NEE'
  | 'JA_NEE_NVT'
  | 'TEKST'
  | 'NUMERIEK'
  | 'FOTO'
  | 'HANDTEKENING'
  | 'SCORE'
  | 'KEUZE'
export type VeiligheidsInvullingStatus = 'CONCEPT' | 'AFGEROND'
/** FO-015: hoe een invulling is ontstaan. */
export type ChecklistInvullingBron = 'DIGITAAL' | 'SCAN' | 'IMPORT'
export type WerkinstructieStatus = 'CONCEPT' | 'GEPUBLICEERD' | 'GEARCHIVEERD'
export type MedewerkerDocStatus = 'INGEDIEND' | 'GOEDGEKEURD' | 'AFGEKEURD'
export type MedewerkerDocZichtbaarheid = 'ALLEEN_MEDEWERKER' | 'IEDEREEN' | 'RELATIE' | 'KLANT'

/** Welke procedure-types horen primair onder Kwaliteit (controlelijsten/inspecties). */
export const KWALITEIT_CONTROLELIJST_TYPES: VeiligheidsProcedureType[] = [
  'CONTROLELIJST_GEREEDSCHAPSKOFFER',
  'CONTROLELIJST_GEREEDSCHAPSWAGEN',
  'INSPECTIE_VOERTUIG',
  'INSPECTIE_PBM',
  'RCA',
]

/** Welke types blijven primair onder Veiligheid (LMRA/TRA/LOTOTO). Eén engine, twee ingangen. */
export const VEILIGHEID_PROCEDURE_TYPES: VeiligheidsProcedureType[] = ['LMRA', 'TRA', 'LOTOTO']

export const PROCEDURE_TYPE_LABELS: Record<VeiligheidsProcedureType, string> = {
  LMRA: 'LMRA',
  TRA: 'TRA',
  LOTOTO: 'LOTOTO',
  CONTROLELIJST_GEREEDSCHAPSKOFFER: 'Controlelijst gereedschapskoffer',
  CONTROLELIJST_GEREEDSCHAPSWAGEN: 'Controlelijst gereedschapswagen',
  INSPECTIE_VOERTUIG: 'Inspectie bedrijfsvoertuig',
  INSPECTIE_PBM: 'Inspectie PBM-set',
  RCA: 'RCA (root cause analysis)',
  TOOLBOX: 'Toolboxbijeenkomst',
}

export const ITEM_TYPE_LABELS: Record<VeiligheidsItemType, string> = {
  JA_NEE: 'Ja / Nee',
  JA_NEE_NVT: 'Ja / Nee / N.v.t.',
  TEKST: 'Tekst',
  NUMERIEK: 'Getal',
  FOTO: 'Foto',
  HANDTEKENING: 'Handtekening',
  SCORE: 'Risicoscore (kans x ernst)',
  KEUZE: 'Keuze uit opties',
}

export type VeiligheidsChecklistItemDTO = {
  id: string
  volgorde: number
  vraag: string
  type: VeiligheidsItemType
  verplicht: boolean
  risicoBijNee: boolean
  sectie: string | null
  opties: string[] | null
  scoreMin: number | null
  scoreMax: number | null
  helpTekst: string | null
}

export type VeiligheidsChecklistTemplateDTO = {
  id: string
  type: VeiligheidsProcedureType
  naam: string
  omschrijving: string | null
  versie: number
  actief: boolean
  // FO-015 periodiek: geldigheids-/herhaaltermijn (maanden) + lead-time (dagen)
  // voor het naderend-vervaldatum-signaal. null = niet-periodiek.
  geldigheidMaanden: number | null
  herinnerDagen: number | null
  items: VeiligheidsChecklistItemDTO[]
}

export type VeiligheidsChecklistAntwoordDTO = {
  id: string
  itemId: string
  vraagSnapshot: string
  antwoordTekst: string | null
  antwoordBool: boolean | null
  antwoordNum: number | null
  fotoPath: string | null
  scoreKans: number | null
  scoreErnst: number | null
  scoreRisico: number | null
  // Server-berekende kleurband van scoreRisico (o.b.v. de KAM-drempels).
  scoreRisicoBand: RisicoBand | null
  antwoordKeuze: string | null
}

export type VeiligheidsChecklistInvullingDTO = {
  id: string
  templateId: string
  templateNaam: string | null
  type: VeiligheidsProcedureType
  ingevuldDoorId: string
  ingevuldDoorNaam: string | null
  status: VeiligheidsInvullingStatus
  afgerondOp: string | null
  werkbonId: string | null
  clientId: string | null
  locatie: string | null
  handtekeningPath: string | null
  // Subject-koppeling (waar gaat de controle over).
  betreftEmployeeId: string | null
  betreftEmployeeNaam: string | null
  voertuigId: string | null
  voertuigLabel: string | null
  voorraadUniekItemId: string | null
  voorraadUniekItemLabel: string | null
  uitrustingItemId: string | null
  uitrustingItemLabel: string | null
  // TRA + RCA.
  hoogsteRisicoScore: number | null
  // Server-berekende kleurband van hoogsteRisicoScore (o.b.v. de KAM-drempels).
  risicoBand: RisicoBand | null
  rcaData: RcaData | null
  // PDF.
  pdfDocumentId: string | null
  heeftPdf: boolean
  // FO-015: bron (digitaal/scan/import), periodieke vervaldatum + status, en de
  // correctie-/versie-keten na aftekenen.
  bron: ChecklistInvullingBron
  vervaltOp: string | null
  // Server-berekende vervalstatus (o.b.v. vervaltOp + template.herinnerDagen).
  vervalStatus: VervalStatus | null
  versie: number
  correctieVanId: string | null
  // true = er is een nieuwere correctie-versie; deze invulling is historisch.
  vervangen: boolean
  // De id van de correctie die deze invulling vervangt (forward-link, voor de
  // versie-keten in het detail). null als deze versie de actuele is.
  vervangenDoorInvullingId: string | null
  antwoorden: VeiligheidsChecklistAntwoordDTO[]
  createdAt: string
}

// ─── RCA (root cause analysis) - vast schema, gedeeld door API + UI ──────────

export const rcaMaatregelSchema = z.object({
  omschrijving: z.string().min(1).max(2000),
  verantwoordelijke: z.string().max(200).nullable().optional(),
  // ISO-datum (yyyy-mm-dd) of leeg; de UI toont 'm in Europe/Amsterdam.
  deadline: z.string().max(40).nullable().optional(),
})
export type RcaMaatregel = z.infer<typeof rcaMaatregelSchema>

export const rcaDataSchema = z.object({
  probleem: z.string().max(4000).default(''),
  // "5x waarom" - keten van oorzaak-vragen (tot 10 toegestaan voor diepere analyses).
  waaroms: z.array(z.string().max(2000)).max(10).default([]),
  grondoorzaken: z.array(z.string().max(2000)).max(20).default([]),
  maatregelen: z.array(rcaMaatregelSchema).max(50).default([]),
})
export type RcaData = z.infer<typeof rcaDataSchema>

// ─── TRA-risicoband (kleurband) - KAM-instelbaar ─────────────────────────────

export type RisicoBand = 'groen' | 'geel' | 'oranje' | 'rood'

export const RISICO_BAND_LABELS: Record<RisicoBand, string> = {
  groen: 'Laag',
  geel: 'Middel',
  oranje: 'Hoog',
  rood: 'Zeer hoog',
}

/**
 * KAM-instelbare TRA-risicodrempels (absolute risicoscore = kans x ernst).
 * Geseed in `KwaliteitChecklistConfig`; KAM kan ze wijzigen via Instellingen.
 * `matrixMax` is de kans/ernst-max (org-matrix) en dient als default voor nieuwe
 * SCORE-items + de invoerlimiet bij invullen.
 */
export interface RisicoDrempels {
  matrixMax: number
  geelVanaf: number
  oranjeVanaf: number
  roodVanaf: number
  hoogRisicoDrempel: number
}

/** Systeem-standaard (5x5-matrix, max 25). Fallback als er (nog) geen config-rij is. */
export const DEFAULT_RISICO_DREMPELS: RisicoDrempels = {
  matrixMax: 5,
  geelVanaf: 7,
  oranjeVanaf: 13,
  roodVanaf: 19,
  hoogRisicoDrempel: 10,
}

/**
 * Kleurband van een TRA-risicoscore o.b.v. de (instelbare) absolute drempels.
 * Server is leidend; UI ontvangt de drempels of een vooraf-berekende band.
 */
export function risicoBandVan(
  scoreRisico: number | null | undefined,
  drempels: RisicoDrempels = DEFAULT_RISICO_DREMPELS,
): RisicoBand | null {
  if (scoreRisico == null) return null
  if (scoreRisico >= drempels.roodVanaf) return 'rood'
  if (scoreRisico >= drempels.oranjeVanaf) return 'oranje'
  if (scoreRisico >= drempels.geelVanaf) return 'geel'
  return 'groen'
}

/** DTO van de KAM-config (de ene actieve rij). */
export type KwaliteitChecklistConfigDTO = RisicoDrempels & { id: string }

// ─── FO-015 bron + periodieke vervalstatus ───────────────────────────────────

export const CHECKLIST_BRON_LABELS: Record<ChecklistInvullingBron, string> = {
  DIGITAAL: 'In de app ingevuld',
  SCAN: 'Scan van papieren lijst',
  IMPORT: 'Bulk-import',
}

/**
 * Periodieke vervalstatus van een (afgeronde) inspectie.
 * - GELDIG: vervaldatum ligt verder weg dan de lead-time.
 * - BINNENKORT: vervaldatum nadert (binnen `herinnerDagen`).
 * - VERLOPEN: vervaldatum is verstreken.
 */
export type VervalStatus = 'GELDIG' | 'BINNENKORT' | 'VERLOPEN'

export const VERVAL_STATUS_LABELS: Record<VervalStatus, string> = {
  GELDIG: 'Geldig',
  BINNENKORT: 'Verloopt binnenkort',
  VERLOPEN: 'Verlopen',
}

export const DEFAULT_HERINNER_DAGEN = 30

/**
 * Bepaalt de vervalstatus o.b.v. de vervaldatum + lead-time. Server is leidend en
 * levert de status vooraf-berekend mee in de DTO; deze helper is de gedeelde bron
 * voor server (cron + DTO) en UI. `now` default = nu (callers in NL-tijd-context).
 */
export function vervalStatusVan(
  vervaltOp: string | Date | null | undefined,
  herinnerDagen: number | null | undefined = DEFAULT_HERINNER_DAGEN,
  now: Date = new Date(),
): VervalStatus | null {
  if (!vervaltOp) return null
  const verval = typeof vervaltOp === 'string' ? new Date(vervaltOp) : vervaltOp
  if (Number.isNaN(verval.getTime())) return null
  if (now.getTime() >= verval.getTime()) return 'VERLOPEN'
  const lead = herinnerDagen ?? DEFAULT_HERINNER_DAGEN
  const drempel = new Date(verval.getTime() - lead * 24 * 60 * 60 * 1000)
  if (now.getTime() >= drempel.getTime()) return 'BINNENKORT'
  return 'GELDIG'
}

/**
 * Vervaldatum van een periodieke inspectie: afrond-datum + geldigheidMaanden
 * (maand-precisie, UTC). null als het type niet-periodiek is (geen geldigheidMaanden).
 * Gedeelde bron voor afronden, scan-/bulk-import en het ontbrekend/verlopen-overzicht.
 */
export function berekenVervaltOp(
  afgerondOp: Date,
  geldigheidMaanden: number | null | undefined,
): Date | null {
  if (!geldigheidMaanden || geldigheidMaanden <= 0) return null
  const d = new Date(afgerondOp.getTime())
  d.setUTCMonth(d.getUTCMonth() + geldigheidMaanden)
  return d
}

// ─── FO-015 ontbrekende/verlopen inspecties (centraal overzicht) ──────────────

/** Welk soort subject een openstaande/verlopen inspectie betreft. */
export type InspectieSubjectKind = 'VOERTUIG' | 'VOORRAAD_UNIEK_ITEM' | 'UITRUSTING' | 'MEDEWERKER'

/** Status van een periodiek-verwachte inspectie die aandacht vraagt. */
export type InspectieOpenstaandStatus = 'ONTBREEKT' | 'BINNENKORT' | 'VERLOPEN'

export const INSPECTIE_OPENSTAAND_LABELS: Record<InspectieOpenstaandStatus, string> = {
  ONTBREEKT: 'Nog nooit uitgevoerd',
  BINNENKORT: 'Verloopt binnenkort',
  VERLOPEN: 'Verlopen',
}

/**
 * Eén rij in het "wat moet nog / wat is verlopen"-overzicht: een subject (voertuig,
 * materieel, PBM-set of medewerker) dat voor een periodiek type een geldige
 * inspectie mist of waarvan de laatste is verlopen / binnenkort vervalt.
 */
export type InspectieOpenstaandDTO = {
  type: VeiligheidsProcedureType
  typeLabel: string
  subjectKind: InspectieSubjectKind
  subjectId: string
  subjectLabel: string
  betreftEmployeeId: string | null
  betreftEmployeeNaam: string | null
  laatsteInvullingId: string | null
  laatsteAfgerondOp: string | null
  vervaltOp: string | null
  status: InspectieOpenstaandStatus
}

// ─── Auto-match medewerker (naam/werknemernummer) ────────────────────────────

export type EmployeeMatchDTO = {
  id: string
  employeeNumber: string | null
  naam: string
  functie: string | null
}

/** Zoekbare subject-optie (materieel/PBM) voor de controlelijst-pickers. */
export type ControlelijstSubjectOptionDTO = {
  id: string
  label: string
  subLabel: string | null
}

// ─── KPI-tegels controlelijsten ──────────────────────────────────────────────

export type ControlelijstStatsDTO = {
  concept: number
  traHoogRestrisico: number
  voertuiginspectiesDezeMaand: number
  totaalAfgerond: number
  // FO-015 veiligheids-kritisch "wat moet nog": verlopen = subjecten met een
  // verstreken periodieke vervaldatum; ontbrekend = subjecten die voor een
  // periodiek type nog nooit een geldige inspectie hadden (voertuig/medewerker-PBM).
  verlopen: number
  ontbrekend: number
}

export type WerkinstructieDTO = {
  id: string
  titel: string
  omschrijving: string | null
  inhoud: string | null
  bestandPath: string | null
  bestandNaam: string | null
  versie: number
  status: WerkinstructieStatus
  clientIds: string[]
  werkbonIds: string[]
  createdAt: string
}

export type MedewerkerDocumentDTO = {
  id: string
  employeeId: string
  employeeNaam: string | null
  titel: string
  omschrijving: string | null
  bestandNaam: string
  status: MedewerkerDocStatus
  zichtbaarheid: MedewerkerDocZichtbaarheid
  clientId: string | null
  beoordeeldOp: string | null
  beoordelingNotitie: string | null
  createdAt: string
}
